/* Shared estimated-delivery formatting.
 *
 * The merchant stores a product-level lead time in the metafield
 * custom.estimated_delivery_date as free text ("1-3 days", "3-5 working days",
 * "1-2 weeks"). This turns that into concrete arrival dates so the PDP and the
 * cart always say the same thing — they used to render from two different
 * sources and could disagree.
 *
 * Contract: any element carrying data-leadtime="<raw>" and containing a <b>
 * gets that <b> replaced with the computed range. Unparseable input is left
 * alone, so the raw text remains as a fallback. Re-running is safe: the raw
 * value stays in the attribute, so the result is idempotent.
 *
 * The same file also formats custom.supplier_restock_date from data-restock,
 * under the opposite fallback rule — unparseable input stays hidden rather than
 * showing raw, because that metafield holds real junk. See parseRestockDate.
 *
 * The lead-time formatting is switchable: the "Estimated delivery" theme setting
 * emits a showDeliveryDates global, and with it off init() leaves the raw metafield
 * text where it is. Restock formatting is deliberately NOT switchable — those values
 * must be validated whatever the merchant has chosen.
 *
 * Exposed as window.YvDeliveryEstimate.{computeRange, parseRestock, init,
 * initRestock}.
 */
(function () {
  'use strict';

  // ---- Estimated delivery: lead-time text -> concrete date range ----
  // Merchant enters product-level lead-times as "N-M unit" (e.g. "1-3 days",
  // "3-5 working days", "1-2 weeks"). Convert to arrival dates like
  // "Tue 23 – Thu 25 Jul". "working days" skip weekends; "days"/"weeks" are
  // calendar. Orders placed after the 12:00 UK cut-off count from the next
  // working day. On any parse failure the raw text is left in place as a
  // fallback.
  function addBusinessDays(date, n) {
    var d = new Date(date.getTime());
    var added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      var dow = d.getDay();
      if (dow !== 0 && dow !== 6) {
        added++;
      }
    }
    return d;
  }

  // Anything ordered after 12:00 is picked the following working day, so the
  // whole estimate shifts by one working day. The cut-off is a fact about the
  // UK warehouse, not about the shopper, so read the hour in Europe/London
  // rather than trusting the browser's zone.
  var CUTOFF_HOUR = 12;

  function ukHour(date) {
    try {
      var h = parseInt(
        new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/London',
          hour: 'numeric',
          hourCycle: 'h23',
        }).format(date),
        10
      );
      // Some engines still report midnight as 24 despite h23; normalise it so
      // the small hours don't read as past the cut-off.
      if (h === 24) h = 0;
      if (!isNaN(h)) return h;
    } catch (e) {
      // No Intl, or no tz database for Europe/London.
    }
    return date.getHours(); // best effort: assume the browser is on UK time
  }

  // Nothing is delivered at the weekend, so roll a Saturday or Sunday result forward
  // to the Monday. Monotonic, so a range's start can never overtake its end; if both
  // land on the same Monday, formatRange collapses them to a single date.
  function nextWeekday(date) {
    var d = new Date(date.getTime());
    while (d.getDay() === 0 || d.getDay() === 6) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  }

  function offsetDate(today, n, isWorking, isWeeks) {
    var d;
    if (isWeeks) {
      // n*5 business days is exactly n calendar weeks whenever the count starts on a
      // weekday, so the everyday result is unchanged from plain n*7 — it only differs
      // when `today` is itself a weekend, which is the case that used to promise
      // "delivery Sun 23rd Aug".
      d = addBusinessDays(today, n * 5);
    } else if (isWorking) {
      d = addBusinessDays(today, n);
    } else {
      d = new Date(today.getTime());
      d.setDate(d.getDate() + n);
    }
    // Still needed on every path: n can be 0 ("0-3 days"), and addBusinessDays(x, 0)
    // returns x unchanged, which before the cut-off can be a Saturday.
    return nextWeekday(d);
  }

  // Build "Fri 31st Jul" from parts — avoids locale-dependent commas in
  // toLocaleDateString('en-GB', {weekday,day,month}) ("Fri, 31 Jul").
  function weekdayOf(date) {
    return date.toLocaleDateString('en-GB', { weekday: 'short' });
  }
  function monthOf(date) {
    return date.toLocaleDateString('en-GB', { month: 'short' });
  }
  // 1 -> "1st", 2 -> "2nd", 3 -> "3rd", 11/12/13 -> "th", else by last digit.
  function ordinal(n) {
    var mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) return n + 'th';
    switch (n % 10) {
      case 1:
        return n + 'st';
      case 2:
        return n + 'nd';
      case 3:
        return n + 'rd';
      default:
        return n + 'th';
    }
  }
  function formatFull(date) {
    return weekdayOf(date) + ' ' + ordinal(date.getDate()) + ' ' + monthOf(date);
  }

  function formatRange(start, end, includeYear) {
    if (start.getTime() === end.getTime()) {
      return formatFull(start) + (includeYear ? ' ' + start.getFullYear() : '');
    }
    var sameMonth =
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear();
    if (sameMonth) {
      // Drop the repeated month on the start: "Tue 23rd – Thu 25th Jul"
      return (
        weekdayOf(start) + ' ' + ordinal(start.getDate()) + ' – ' + formatFull(end) + (includeYear ? ' ' + end.getFullYear() : '')
      );
    }
    return formatFull(start) + (includeYear ? ' ' + start.getFullYear() : '') + ' – ' + formatFull(end) + (includeYear ? ' ' + end.getFullYear() : '');
  }

  function computeDeliveryRange(raw, orderedAt) {
    if (typeof raw !== 'string' || !raw) return null;
    var m = raw.match(/^\s*(\d+)\s*[-–]\s*(\d+)\s+(.+?)\s*$/);
    if (!m) return null;
    var min = parseInt(m[1], 10);
    var max = parseInt(m[2], 10);
    if (isNaN(min) || isNaN(max) || max < min) return null;
    var unit = m[3].toLowerCase();
    var isWeeks = unit.indexOf('week') !== -1;
    var isWorking = unit.indexOf('working') !== -1;
    var anchored = orderedAt !== undefined;
    if (anchored && (max > 520 || !/^(?:working\s+)?(?:days?|weeks?)$/.test(unit))) return null;
    var now = anchored ? new Date(orderedAt) : new Date();
    if (!Number.isFinite(now.getTime()) || (anchored && typeof orderedAt !== 'string')) return null;
    var from = new Date(now.getTime());
    if (anchored) {
      try {
        var parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(now);
        var civil = {};
        parts.forEach(function (part) { civil[part.type] = part.value; });
        from = new Date(Number(civil.year), Number(civil.month) - 1, Number(civil.day));
      } catch (e) {
        return null;
      }
    }
    from.setHours(0, 0, 0, 0);
    // Past the cut-off the count starts from the next working day, so a
    // Friday or weekend afternoon lands on Monday.
    if (ukHour(now) >= CUTOFF_HOUR) {
      from = addBusinessDays(from, 1);
    }
    var start = offsetDate(from, min, isWorking, isWeeks);
    var end = offsetDate(from, max, isWorking, isWeeks);
    return formatRange(start, end, anchored);
  }

  // ---- Supplier restock date: raw metafield -> "Fri 31st Oct" ----
  // custom.supplier_restock_date is free text on ~8,600 products and the values are a
  // mess of whatever each supplier feed happened to send. Sampling the live catalogue
  // turns up, in rough order of frequency: "Default test value", "-", ISO
  // ("2026-10-31"), ISO with a midnight stamp ("2026-11-09T00:00:00", ~10% of values),
  // UK-ordered ("09-1-2026"), and ISO year-week ("2026-33").
  //
  // Everything but the year-week form is understood. "2026-33" is rejected on purpose:
  // it cannot be told apart from a year-month for weeks 1-12, and guessing wrong would
  // put a date months out in front of a shopper. Hidden is the honest answer until
  // those values are cleaned up in the admin.
  //
  // Returns a Date only for a real date still to come. Rejecting past dates is what
  // disposes of the 1905 sentinel, so that needs no special case of its own.
  //
  // The caller hides anything this rejects — the opposite of the lead-time rule above,
  // where unparseable input keeps its raw text because "3-5 working days" still reads
  // fine. "Estimated restock Default test value" does not.
  function parseRestockDate(raw) {
    if (!raw) return null;
    var text = String(raw).trim();
    var y;
    var mo;
    var day;

    // The trailing group swallows the "T00:00:00" some feeds append. The time is always
    // midnight and always local to the supplier, so there is nothing in it worth reading.
    var iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ][\d:.+\-Z]*)?$/);
    var uk = text.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (iso) {
      y = parseInt(iso[1], 10);
      mo = parseInt(iso[2], 10);
      day = parseInt(iso[3], 10);
    } else if (uk) {
      // British order, because the merchant enters British dates. Unambiguous at
      // "30-6-2025", a guess at "6-7-2025" — but a consistent guess.
      day = parseInt(uk[1], 10);
      mo = parseInt(uk[2], 10);
      y = parseInt(uk[3], 10);
    } else {
      return null;
    }

    var d = new Date(y, mo - 1, day);
    d.setHours(0, 0, 0, 0);
    // Catches impossible dates: new Date(2026, 1, 31) silently rolls into March, so
    // the components come back different from the ones that went in.
    if (d.getFullYear() !== y || d.getMonth() !== mo - 1 || d.getDate() !== day) {
      return null;
    }

    var today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d.getTime() < today.getTime()) return null;

    return d;
  }

  // Contract: any element carrying data-restock="<raw>" and containing a <b> gets the
  // formatted date written into that <b>, and only then loses its `hidden` attribute.
  // Anything unparseable is left hidden, so the raw value can never reach the shopper.
  // Whether a revealed line is actually on screen is a separate question, answered in
  // CSS off the is-*-stock modifier — see initDeliveryBand() in assets/pdp-redesign.js.
  function initRestockDate(scope) {
    var nodes = (scope || document).querySelectorAll('[data-restock]');
    Array.prototype.forEach.call(nodes, function (el) {
      var date = parseRestockDate(el.getAttribute('data-restock'));
      if (!date) return;
      var b = el.querySelector('b');
      if (!b) return;
      var formatted = formatFull(date);
      if (b.textContent !== formatted) {
        b.textContent = formatted;
      }
      el.removeAttribute('hidden');
    });
  }

  function initDeliveryEstimate(scope) {
    // The "Estimated delivery" theme setting, emitted as a global by
    // snippets/theme-scripts.liquid. Switched off, there is nothing to do: the raw
    // metafield text is already sitting in the <b> from Liquid, so leaving it alone IS
    // "show the value, not the date". Undefined counts as on, so the asset still formats
    // if it ever loads somewhere theme-scripts.liquid has not run.
    //
    // Gated here rather than in computeDeliveryRange because this is the one choke point
    // every caller goes through — run(), the cart MutationObserver, and pdp-redesign.js —
    // and it leaves computeRange usable from the console either way.
    if (typeof showDeliveryDates !== 'undefined' && showDeliveryDates === false) return;

    var nodes = (scope || document).querySelectorAll('[data-leadtime]');
    Array.prototype.forEach.call(nodes, function (el) {
      var formatted = computeDeliveryRange(el.getAttribute('data-leadtime'));
      if (!formatted) return; // leave raw fallback text untouched
      var b = el.querySelector('b');
      // Compare before writing: assigning textContent always replaces the child
      // node, which is a childList mutation. Under the observer below that would
      // re-trigger this function forever. This makes the write idempotent at the
      // DOM level, not just in the resulting string.
      if (b && b.textContent !== formatted) {
        b.textContent = formatted;
      }
    });
  }

  window.YvDeliveryEstimate = {
    computeRange: computeDeliveryRange,
    parseRestock: parseRestockDate,
    init: initDeliveryEstimate,
    initRestock: initRestockDate,
  };

  function run() {
    initDeliveryEstimate(document);
    initRestockDate(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // The cart page replaces the innerHTML of [data-cart-wrapper] on every ajax
  // update (see cartPageUpdate in cart-ajax.js), which discards the formatted
  // dates. Re-format whatever gets swapped in. Observing here rather than
  // patching cart-ajax.js keeps the two concerns independent.
  var cartWrapper = document.querySelector('[data-cart-wrapper]');
  if (cartWrapper && typeof MutationObserver !== 'undefined') {
    var opts = { childList: true, subtree: true };
    var observer = new MutationObserver(function () {
      // Detach around our own DOM writes so they can't re-enter this callback.
      // Dropping queued records is safe: init() re-scans the whole scope, so
      // anything added while detached is still picked up.
      observer.disconnect();
      initDeliveryEstimate(cartWrapper);
      initRestockDate(cartWrapper);
      observer.observe(cartWrapper, opts);
    });
    observer.observe(cartWrapper, opts);
  }
})();
